import { HttpClient } from "@angular/common/http";
import { Injectable, inject } from "@angular/core";
import { IAccountReceivable, ICreateSale, ICreateSaleResquest, ICreditCard, IDeleteCreditCard, IIp, IPaguelaSale, IReceivement, ISubscription, ProfileSubscriptionListItem, ShortenedLinkResponse, TPaguelaCardPayload, TPaguelaCardResponse, TSetRecurrenceParams, TSubscriptionResponse } from '@app-payments/data-access/entities/payments.interface';
import { catchError, map, Observable, of } from "rxjs";
import { EntityPath, Protocol, url } from "src/environments/environment";
import { IAuthorization, IPaguelaAccount, IToken, ISale } from "@app-payments/data-access/entities/payments.interface";
import * as signalR from '@microsoft/signalr';
import { TranslateAdapter } from "src/shared/helpers/translateAdapter";
import { SubscriptionDictionary } from "../entities/payment.dictionary";
import { AuthStore } from "@app-auth/data-access/auth.store";
import { HttpTransportType } from "@microsoft/signalr";
@Injectable({
    providedIn: 'root'
}) export class PaymentsService {
    private http = inject(HttpClient);
    private subscriptionDictionary = new TranslateAdapter(SubscriptionDictionary);
    private authStore = inject(AuthStore);
    private readonly endpoint = 'https://link.actuar.com/';
    private readonly defaultSource = 'actuar';

    public getToken(tokenId: string): Observable<IToken> {
        return this.http.get<IToken>(url(Protocol.https, EntityPath.paguela, `/Token/ObterPorId`), {
            withCredentials: false,
            params: { id: tokenId },
        });
    }

    public getPaguelaAccount(contaId: string): Observable<IPaguelaAccount> {
        return this.http.get<IPaguelaAccount>(url(Protocol.https, EntityPath.paguela, `/Contas/ObterPorId`), {
            withCredentials: false,
            params: { id: contaId },
        });
    }

    public proccessToken(id: string, authorization: IAuthorization): Observable<ISale> {
        return this.http.post<ISale>(url(Protocol.https, EntityPath.paguela, `/Token/Processar`), authorization, {
            params: { id: id },
            withCredentials: false,
        });
    }

    public getAccountIdPaguelaByPersonId(personId: string): Observable<string> {
        return this.http.get<string>(url(Protocol.https, EntityPath.base, '/Pessoas/ObterPagueLaPessoaIdPorPessoaId'), {
            params: {
                pessoaId: personId
            },
            withCredentials: false
        });
    }

    public getPaguelaSituation(accountId: string): Observable<{ Situacao: number }> {
        return this.http.get<{ Situacao: number }>(url(Protocol.https, EntityPath.paguela, '/Contas/ObterSituacao'), {
            params: {
                id: accountId
            },
            withCredentials: false
        });
    }

    public checkGymHasPix(personId: string): Observable<boolean> {
        return this.http.get<boolean>(url(Protocol.https, EntityPath.base, '/paguela/PossuiServicoPixAtivo'), {
            params: {
                pessoaId: personId
            },
            withCredentials: true
        });
    }

    public getBillsToPay(personId: string): Observable<IAccountReceivable[]> {
        return this.http.get<IAccountReceivable[]>(url(Protocol.https, EntityPath.receivable_service, '/AccountReceivable/GetAccountsReceivablesDetailedByPersonId'), {
            params: {
                personId
            },
            withCredentials: true
        });
    }

    public getPaidBills(personId: string): Observable<IReceivement[]> {
        return this.http.get<IReceivement[]>(url(Protocol.https, EntityPath.receivable_service, '/Receivement/GetReceivementByPersonId'), {
            params: {
                personId
            },
            withCredentials: true
        });
    }

    public getSubscriptions(personId: string): Observable<ISubscription[]> {
        return this.http.get<ISubscription[]>(url(Protocol.https, EntityPath.base, '/Assinaturas/ObterAssinaturaPessoaPorPessoaIdTreino'), {
            params: {
                personId
            },
            withCredentials: true
        });
    }

    public getSubscriptionsByPerson(personId: string): Observable<ISubscription[]> {
        return this.http.get<ProfileSubscriptionListItem[]>
            (url(Protocol.https, EntityPath.subscription_service, '/Subscription/GetSubscriptionsByPerson'),
                {
                    params: { personId },
                    withCredentials: true
                }).pipe(map((e: any) => {
                    console.log(e)
                    return e.map((element: any) => {
                        element['DataCadastro'] = element.StartOnUtc;
                        return this.subscriptionDictionary.translate(element);
                    });
                }));
    }

    public getPagueLaSale(id: string): Observable<IPaguelaSale> {
        return this.http.get<IPaguelaSale>(url(Protocol.https, EntityPath.paguela, '/Vendas/ObterPorId'), {
            params: {
                id
            },
            withCredentials: true
        });
    }

    public getCardById(cartaoId: string): Observable<ICreditCard> {
        return this.http.get<ICreditCard>(url(Protocol.https, EntityPath.paguela, '/Cartoes/ObterPorId'), {
            params: {
                cartaoId
            },
            withCredentials: true
        });
    }

    public getCardsByClientId(
        clienteId: string,
        contaId?: string | null
    ): Observable<ICreditCard[]> {
        const params: Record<string, string> = { clienteId };
        if (contaId) params['contaId'] = contaId;

        return this.http.get<ICreditCard[]>(url(Protocol.https, EntityPath.paguela, '/Cartoes/ObterPorClienteId'),
            {
                params,
            });
    }

    public setRecurrence(data: TSetRecurrenceParams): Observable<any> {
        return this.http.post<any>(url(Protocol.https, EntityPath.subscription_service, '/Subscription/SetRecurrence'), data, {
            withCredentials: true
        });
    }

    /**
     * Sends a new credit card to the Paguela API and returns the raw response.
     */
    public addCard(payload: TPaguelaCardPayload): Observable<TPaguelaCardResponse> {
        return this.http.post<TPaguelaCardResponse>
            (url(Protocol.https, EntityPath.paguela, '/Cartoes/Adicionar'),
                payload);
    }


    public createSaleCard(data: ICreateSaleResquest): Observable<ICreateSale> {
        return this.http.post<ICreateSale>(url(Protocol.https, EntityPath.receivable_service, '/SalesOperations/CreateCardSale'), data, {
            withCredentials: true
        });
    }

    public createSalePix(data: ICreateSaleResquest): Observable<ICreateSale> {
        return this.http.post<ICreateSale>(url(Protocol.https, EntityPath.receivable_service, '/SalesOperations/CreatePixSale'), data, {
            withCredentials: true
        });
    }

    public deleteCard(cardId: string): Observable<IDeleteCreditCard> {
        return this.http.delete<IDeleteCreditCard>(url(Protocol.https, EntityPath.paguela, '/Cartoes/Remover'), {
            params: {
                id: cardId
            }
        });
    }

    public getSubscriptionById(id: string): Observable<TSubscriptionResponse> {
        return this.http.get<TSubscriptionResponse>
            (url(Protocol.https, EntityPath.subscription_service, '/Subscription'),
                {
                    params: { id },
                    withCredentials: true,
                });
    }


    public shortenLink(link: string): Observable<string> {
        return this.http.post<string>(`${Protocol.https}${EntityPath.shortener}`, {
            originalLink: link,
            sourceApi: 'paguela'
        }, {
            responseType: 'text' as 'json'
        });
    }

    public getIp(): Observable<IIp> {
        return this.http.get<IIp>('https://api.ipify.org/?format=json');
    }

    public getConnection(accountId: string): signalR.HubConnection {
        return new signalR.HubConnectionBuilder()
            .withUrl(`${Protocol.https}${EntityPath.client_notification}/NotificationHub?ContaId=${accountId}`,
                { skipNegotiation: true, transport: signalR.HttpTransportType.WebSockets })
            .withAutomaticReconnect([5000, 5000, 5000, 10000])
            .build()
    }

    public getAccountsReceivablesByPersonSubscriptionId(personSubscriptionId: string): Observable<IAccountReceivable[]> {
        return this.http.get<Array<IAccountReceivable & { AccountReceivableId: string }>>(url(Protocol.https, EntityPath.receivable_service, '/AccountReceivable/GetAccountsReceivablesByPersonSubscriptionId'), {
            params: {
                personSubscriptionId
            },
            withCredentials: true
        }).pipe(map((data) => data.map((item) => {
            const { AccountReceivableId, AmountFineCurrent, CurrentInterestAmount, CurrentOpenAmount, OriginalAmount, DueOn, PersonSubscriptionId, PersonSubscriptionRenewalId, SourceDescription, SourceType } = item;
            return {
                AccountsReceivableId: AccountReceivableId,
                AmountFineCurrent,
                CurrentInterestAmount,
                CurrentOpenAmount,
                OriginalAmount,
                DueOn,
                PersonSubscriptionId,
                PersonSubscriptionRenewalId,
                SourceDescription,
                SourceId: PersonSubscriptionId,
                SourceType,
            };
        })));
    }

    public openConnection(eventNames: string[]): Observable<any> {
        return new Observable((obs) => {
            const auth = this.authStore?.AuthData()!;
            const connection = new signalR.HubConnectionBuilder().withUrl(url(Protocol.https, EntityPath.client_notification, `/NotificationHub/?TenantId=${auth?.TenantId}&UserId=${auth?.PersonId}`), {
                transport: HttpTransportType.WebSockets,
                skipNegotiation: true,
                withCredentials: false
            })
                .withAutomaticReconnect([5000, 5000, 5000, 10000])
                .build();

            const subsc = new Observable((observer) => {
                for (const eventName of eventNames) {
                    connection.on(eventName, (data) => {
                        const eventData: any = {};

                        if (eventName === 'onSubscriptionReceivementNotification') {
                            eventData['Receivement'] = { ...data};
                        }

                        if (eventName === 'onReceivementErrorNotification') {
                            eventData['ReceivementError'] = { ...data };
                        }
                    
                        observer.next({
                            eventName,
                            eventData,
                        });
                    });
                }

                connection.start().then(() => {
                    observer.next({
                        eventName: 'onConnected',
                        eventData: 'ok',
                    });
                }).catch((error) => {
                    observer.error(error);
                });

                connection.onclose((e) => {
                    observer.next({
                        eventName: 'onConnectionClose',
                        eventData: e,
                    });
                });

                connection.onreconnecting((e) => {
                    observer.next({
                        eventName: 'onConnectionReconnecting',
                        eventData: e,
                    });
                });

                connection.onreconnected((e) => {
                    observer.next({
                        eventName: 'onConnectionReconnected',
                        eventData: e,
                    });
                });
            });

            subsc.subscribe({
                next: (res) => {
                    obs.next(res);
                },
                error: (error) => {
                    obs.next(error);
                    obs.complete();
                },
            });

            return function unsubscribe() {
                connection.stop();
            };
        });
    }

    getShortLink(
        originalLink: string,
        sourceApi: string = this.defaultSource
    ): Observable<ShortenedLinkResponse | null> {
        if (!originalLink) {
        return of(null);
        }

        const payload = { originalLink, sourceApi };

        return this.http
        .post<string>(this.endpoint, payload, {
            responseType: 'text' as 'json',
        })
        .pipe(
            map((code) => this.normalizeResponse(code)),
            catchError(() => of(null))
        );
    }

    private normalizeResponse(code: string): ShortenedLinkResponse | null {
        if (!code) {
        return null;
        }

        const normalizedCode = code.trim().replace(/^"|"$/g, '');

        if (!normalizedCode) {
        return null;
        }

        return {
        code: normalizedCode,
        shortUrl: `${this.endpoint}${normalizedCode}`,
        };
    }
}